import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { UserCog, Plus, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', specializations: [] });

  const fetchStaff = async () => {
    try { const res = await api.get('/admin/staff'); setStaff(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) { toast.error('Name, email, and password required'); return; }
    try {
      await api.post('/admin/staff', { ...form, specializations: form.specializations });
      toast.success('Staff member added');
      setDialogOpen(false);
      setForm({ email: '', password: '', name: '', phone: '', specializations: [] });
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create staff');
    }
  };

  const toggleActive = async (userId, current) => {
    try {
      await api.put(`/admin/staff/${userId}`, { is_active: !current });
      fetchStaff();
      toast.success(`Staff ${!current ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Staff Management</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{staff.length} staff members</p>
        </div>
        <Button className="rounded-full bg-[#2D2D2D] text-white hover:bg-[#1A1A1A] shadow-md" onClick={() => setDialogOpen(true)} data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Staff
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {staff.map(s => (
          <Card key={s.user_id} className={`rounded-2xl border-[#E5E5E0] bg-white card-hover ${!s.is_active ? 'opacity-60' : ''}`} data-testid={`staff-card-${s.user_id}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[#E2E8E4] rounded-2xl flex items-center justify-center text-lg font-bold text-[#2D2D2D]">
                  {s.name?.[0]}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#2D2D2D]">{s.name}</h3>
                  <p className="text-sm text-[#5A5A5A]">{s.email}</p>
                </div>
              </div>
              {s.specializations?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {s.specializations.map((spec, i) => (
                    <span key={i} className="text-xs bg-[#F5F5F0] text-[#5A5A5A] px-2.5 py-1 rounded-full">{spec}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#888] flex items-center gap-1"><CalendarCheck className="w-3 h-3" />{s.appointment_count} appointments</span>
                <Button variant={s.is_active ? 'outline' : 'default'} size="sm" className="rounded-full text-xs" onClick={() => toggleActive(s.user_id, s.is_active)} data-testid={`toggle-staff-${s.user_id}`}>
                  {s.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add Staff Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-name-input" /></div>
            <div><Label className="text-sm">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-email-input" /></div>
            <div><Label className="text-sm">Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-password-input" /></div>
            <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-phone-input" /></div>
            <div><Label className="text-sm">Specializations (comma separated)</Label><Input value={form.specializations.join(', ')} onChange={(e) => setForm({ ...form, specializations: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="mt-1 rounded-lg" data-testid="staff-specs-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={handleCreate} data-testid="save-staff-btn">Add Staff</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
